// src/modules/app-views/invoices/pages/Init/modals/FiscalBootstrapModal/Step1.jsx
// *******************************************************************************
// STEP 1 - DIGITAL CERTIFICATE
// *******************************************************************************
// First step: Upload digital certificate. User cannot proceed without a valid
// certificate attached to the company.
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useCallback, useState, useEffect } from 'react';
import { Alert, Grid } from 'antd';
import { SafetyCertificateOutlined } from '@ant-design/icons';
import { openModal } from 'components/core/GenericModal';
import CertificateUploadModal from 'modules/app-views/account/pages/Subscription/modals/CertificateUploadModal';
import CertificateCard from 'modules/app-views/fiscalConfig/pages/Document/tabs/CertificateTab/CertificateCard';
import translateX from 'utils/translateX';
import utils from 'utils/utils';
import useZustand from 'hooks/useZustand';
import authService from 'services/authService';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const { useBreakpoint } = Grid;

// *******************************************************************************
// STEP CONTENT COMPONENT
// *******************************************************************************
const Step1Content = ({ entityId, company, localCertificate, onCertificateSuccess }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const storedSubscription = useZustand('auth', 'subscription');

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const isMobile = !utils.getBreakPoint(useBreakpoint()).includes('sm');

  // Certificate data: use local (just uploaded) or from company
  const certificate = localCertificate || company?.fiscal?.certificate || null;

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [displayCertificate, setDisplayCertificate] = useState(certificate);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (localCertificate) {
      setDisplayCertificate(localCertificate);
    } else if (company?.fiscal?.certificate) {
      setDisplayCertificate(company.fiscal.certificate);
    }
  }, [localCertificate, company?.fiscal?.certificate]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleUploadCertificate = useCallback(() => {
    const companyCnpj = company?.cnpj?.value || company?.cnpj;

    openModal(CertificateUploadModal, {
      entityId,
      companyCnpj,
      onSuccess: async ({ certificateData, stateRegistrationData }) => {
        // Update local state
        setDisplayCertificate(certificateData);

        // Pass both certificateData and stateRegistrationData to parent
        onCertificateSuccess?.({ certificateData, stateRegistrationData });

        // Refresh auth state if main company
        if (entityId && storedSubscription?.entityId === entityId) {
          await authService.signInRefresh();
        }
      },
    });
  }, [entityId, company, storedSubscription, onCertificateSuccess]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 9. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div style={{ paddingTop: 8 }}>
      {/* Info Alert */}
      <Alert
        type="info"
        showIcon
        icon={<SafetyCertificateOutlined />}
        style={{ marginBottom: 24 }}
        title={translateX('fiscal_module_setup_required')}
        description={translateX('fiscal_module_setup_required_description')}
      />

      {/* Certificate Card - Reusing from fiscalConfig */}
      <CertificateCard
        displayCertificate={displayCertificate}
        isMobile={isMobile}
        locked={false}
        onUpload={handleUploadCertificate}
        onDelete={null} // No delete in bootstrap flow
      />
    </div>
  );
};

// *******************************************************************************
// STEP CONFIGURATION
// *******************************************************************************
const step1 = ({ entityId, company, localCertificate, onCertificateSuccess }) => ({
  stepTitle: 'digital_certificate',
  stepTooltip: 'upload_digital_certificate',
  content: () => (
    <Step1Content
      entityId={entityId}
      company={company}
      localCertificate={localCertificate}
      onCertificateSuccess={onCertificateSuccess}
    />
  ),
  // Validation: cannot proceed without certificate
  onNext: async () => {
    // Check if certificate exists (either local or from company)
    const hasCertificate = !!(
      localCertificate ||
      company?.fiscal?.certificate ||
      company?.fiscalData?.certificate
    );

    if (!hasCertificate) {
      // Show error message - user must upload certificate
      const { default: appService } = await import('services/appService');
      appService.message({
        flag: 'warning',
        text: 'upload_certificate_to_continue',
        duration: 4,
      });
      return false; // Prevent navigation
    }

    return true; // Allow navigation
  },
});

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default step1;
